import { ApolloError, gql, useQuery } from '@apollo/client';
import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import {
  ignorePermissionsErrors,
  ignorePlanErrors,
} from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  Graph__GraphSettingsAccountListQuery,
  Graph__GraphSettingsAccountListQueryVariables,
  Graph__GraphSettingsQuery,
  Graph__GraphSettingsQueryVariables,
} from 'src/lib/graphqlTypes/types';

import { GraphRef } from '../../hooks/useGraphRef';
import { BuildConfigCard } from '../../variantSettingsPage/variantSettingsCards/BuildConfigCard';
import { FederationVersionInfoFragment } from '../../variantSettingsPage/variantSettingsCards/FederationVersionInfo';
import {
  PersistedQueriesConfigCard,
  persistedQueriesConfigCardServiceFragment,
  persistedQueriesConfigCardVariantFragment,
} from '../../variantSettingsPage/variantSettingsCards/PersistedQueriesConfigCard';

import {
  DeleteGraphCard,
  DeleteGraphCardServiceFragment,
} from './deleteGraphCard/DeleteGraphCard';
import {
  GeneralGraphSettingsCard,
  GeneralGraphSettingsCardFragment,
} from './GeneralGraphSettingsCard';
import { GraphOwnerCard } from './graphOwnerCard/GraphOwnerCard';
import {
  ServiceGraphOwnerCardFragment,
  UserGraphOwnerCardFragment,
} from './graphOwnerCard/graphOwnerCardQueries';
import {
  GraphVisibilityCard,
  GraphVisibilityCardFragment,
} from './graphVisibilityCard/GraphVisibilityCard';

export const GeneralSettingsPageQuery = gql`
  query Graph__GraphSettingsQuery($graphVariant: String!, $serviceId: ID!) {
    service(id: $serviceId) {
      id
      ...GraphVisibilityCardFragment
      ...GeneralGraphSettingsCardFragment
      ...ServiceGraphOwnerCardFragment
      ...DeleteGraphCardServiceFragment
      ...PersistedQueriesConfigCardServiceFragment
      defaultBuildPipelineTrackUpdatedAt
      roles {
        canQueryPrivateInfo
        canManageBuildConfig
      }
      variant(name: $graphVariant) {
        id
        buildConfigUpdatedAt
        allowedTracks {
          badge
          buildPipelineTrack
          compositionVersion
          deprecatedAt
          displayName
          minimumGatewayVersion
          minimumRouterVersion
          notSupportedAt
        }
        router {
          id
          routerVersion {
            core
          }
        }
        ...FederationVersionInfoFragment
        ...PersistedQueriesConfigCardVariantFragment
      }
    }
  }
  ${GraphVisibilityCardFragment}
  ${GeneralGraphSettingsCardFragment}
  ${ServiceGraphOwnerCardFragment}
  ${DeleteGraphCardServiceFragment}
  ${persistedQueriesConfigCardVariantFragment}
  ${persistedQueriesConfigCardServiceFragment}
  ${FederationVersionInfoFragment}
`;

const GetAccountListQuery = gql`
  query Graph__GraphSettingsAccountListQuery {
    me {
      id
      ... on User {
        id
        ...UserGraphOwnerCardFragment
      }
    }
  }
  ${UserGraphOwnerCardFragment}
`;

export const GeneralSettingsPage = ({ graphRef }: { graphRef: GraphRef }) => {
  const graphId = graphRef.graphId;
  const { graphVariant } = graphRef;

  const { data, loading, error, refetch } = useQuery<
    Graph__GraphSettingsQuery,
    Graph__GraphSettingsQueryVariables
  >(GeneralSettingsPageQuery, {
    variables: {
      graphVariant,
      serviceId: graphId,
    },
    context: appLinkContext({ catchErrors: [ignorePlanErrors] }),
  });
  const {
    data: accountList,
    loading: accountListLoading,
    error: accountListError,
  } = useQuery<
    Graph__GraphSettingsAccountListQuery,
    Graph__GraphSettingsAccountListQueryVariables
  >(GetAccountListQuery, {
    variables: {},
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const [actionError, setActionError] = useState<ApolloError | null>(null);

  if (error || accountListError) {
    return (
      <div className="flex flex-1 items-center justify-center py-6">
        <div className="max-w-md text-center">
          <div className="mb-4 text-3xl">Error loading settings</div>
          <div className="mb-4 text-placeholder">
            {error ? error.message : ''}
            {accountListError ? accountListError.message : ''}
          </div>
        </div>
      </div>
    );
  }

  if (loading || accountListLoading) {
    return <Loading />;
  }
  if (!data || !data.service || accountList?.me?.__typename !== 'User') {
    return (
      <div className="flex flex-1 items-center justify-center py-6">
        <div className="max-w-md text-center">
          <div className="mb-4 text-3xl">Error loading settings</div>
          <div className="mb-4 text-placeholder">
            There was a problem retrieving the data for to populate this page.
          </div>
        </div>
      </div>
    );
  }

  if (
    !data.service.account ||
    !data.service.roles?.canQueryPrivateInfo ||
    !data.service.variant
  ) {
    return <NotFound />;
  }

  return (
    <>
      <GeneralGraphSettingsCard service={data.service} refetchData={refetch} />
      {data.service.graphType !== GraphQLTypes.GraphType.CLOUD_SUPERGRAPH && (
        <BuildConfigCard
          isGraph
          serviceId={graphId}
          variant={data.service.variant}
          canManageBuildConfig={Boolean(
            data.service.roles.canManageBuildConfig,
          )}
          graphType={data.service.graphType}
          defaultBuildPipelineTrack={data.service.defaultBuildPipelineTrack}
          defaultBuildPipelineTrackUpdatedAt={
            data.service.defaultBuildPipelineTrackUpdatedAt
          }
          refetchData={refetch}
          routerVersion={data.service.variant.router?.routerVersion?.core}
        />
      )}

      {data.service.graphType !== GraphQLTypes.GraphType.CLOUD_SUPERGRAPH && (
        <PersistedQueriesConfigCard
          settingsLevel="graph"
          graph={data.service}
        />
      )}

      <Heading className="pt-4" variant="heading" size="2xl">
        🚧 Danger zone
      </Heading>
      <GraphVisibilityCard dataFragment={data.service} />
      <GraphOwnerCard
        service={data.service}
        memberships={accountList.me.memberships}
        setError={setActionError}
      />
      <DeleteGraphCard service={data.service} setError={setActionError} />
      {actionError && (
        <Modal isOpen size="lg" onClose={() => setActionError(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Error</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="w-full rounded bg-red-lightest px-4 py-3 text-base leading-normal text-red-dark">
                <div>An error occurred: {actionError.message}</div>
                <div>Please try again</div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="primary" onClick={() => setActionError(null)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
